import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { mediaUrl, formatPrice } from "@/lib/api";
import api, { apiError } from "@/lib/api";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import { toast } from "sonner";

export default function Cart() {
    const { items, updateQty, remove, subtotal } = useCart();
    const navigate = useNavigate();
    const [coupon, setCoupon] = useState("");
    const [applied, setApplied] = useState(null);
    const [applying, setApplying] = useState(false);

    const applyCoupon = async () => {
        if (!coupon.trim()) return;
        setApplying(true);
        try {
            const { data } = await api.post("/coupons/validate", { code: coupon.trim(), subtotal });
            setApplied(data);
            toast.success(`Coupon applied — you saved ${formatPrice(data.discount)}`);
        } catch (err) {
            setApplied(null);
            toast.error(apiError(err));
        } finally {
            setApplying(false);
        }
    };

    const discount = applied?.discount || 0;
    const total = Math.max(0, subtotal - discount);

    if (items.length === 0) {
        return (
            <div className="mx-auto max-w-2xl px-5 py-28 text-center">
                <ShoppingBag className="h-12 w-12 mx-auto text-gold" strokeWidth={1} />
                <h1 className="font-serif text-3xl mt-6">Your cart is empty</h1>
                <p className="text-muted-foreground mt-3">Discover premium styling tools crafted to elevate your beauty.</p>
                <Link to="/shop" className="inline-block mt-8 bg-gold text-white px-8 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors">Shop Now</Link>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10 md:py-14">
            <h1 className="font-serif text-4xl tracking-tight mb-10">Shopping Cart</h1>
            <div className="grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-6">
                    {items.map((it) => (
                        <div key={it.product_id} className="flex gap-4 border-b border-[#E8DFD5] pb-6" data-testid={`cart-item-${it.product_id}`}>
                            <Link to={`/products/${it.snap.slug}`} className="shrink-0 h-28 w-24 bg-cream-soft overflow-hidden">
                                {it.snap.image ? <img src={mediaUrl(it.snap.image)} alt={it.snap.name} className="w-full h-full object-cover" /> : <ProductPlaceholder className="w-full h-full" label="" />}
                            </Link>
                            <div className="flex-1">
                                <div className="flex justify-between gap-4">
                                    <Link to={`/products/${it.snap.slug}`} className="font-medium hover:text-gold transition-colors">{it.snap.name}</Link>
                                    <button data-testid={`cart-remove-${it.product_id}`} onClick={() => { remove(it.product_id); toast.success("Removed from cart"); }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </div>
                                <div className="flex items-baseline gap-2 mt-1">
                                    <span className="font-semibold">{formatPrice(it.snap.price)}</span>
                                    {it.snap.mrp > it.snap.price && <span className="text-xs text-muted-foreground line-through">{formatPrice(it.snap.mrp)}</span>}
                                </div>
                                <div className="flex items-center justify-between mt-4">
                                    <div className="flex items-center border border-ink">
                                        <button data-testid={`cart-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.qty - 1)} className="p-2 hover:bg-cream-soft"><Minus className="h-3.5 w-3.5" /></button>
                                        <span className="w-10 text-center text-sm">{it.qty}</span>
                                        <button data-testid={`cart-inc-${it.product_id}`} onClick={() => updateQty(it.product_id, it.qty + 1)} className="p-2 hover:bg-cream-soft"><Plus className="h-3.5 w-3.5" /></button>
                                    </div>
                                    <span className="font-medium">{formatPrice(it.snap.price * it.qty)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                    <Link to="/shop" className="inline-block text-sm text-gold hover:underline">← Continue Shopping</Link>
                </div>

                <div className="bg-cream-soft p-6 h-fit">
                    <h2 className="font-serif text-xl mb-5">Order Summary</h2>
                    <div className="flex gap-2 mb-5">
                        <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" className="flex-1 bg-white border border-[#E8DFD5] px-3 py-2.5 text-sm outline-none focus:border-gold uppercase" />
                        <button data-testid="coupon-apply" onClick={applyCoupon} disabled={applying} className="bg-ink text-cream px-4 text-sm flex items-center gap-1.5 hover:bg-gold transition-colors"><Tag className="h-3.5 w-3.5" /> Apply</button>
                    </div>
                    <div className="space-y-3 text-sm border-t border-[#E8DFD5] pt-4">
                        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                        {discount > 0 && <div className="flex justify-between text-gold"><span>Coupon ({applied.code})</span><span>− {formatPrice(discount)}</span></div>}
                        <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>Calculated at checkout</span></div>
                        <div className="flex justify-between font-semibold text-base border-t border-[#E8DFD5] pt-3"><span>Total</span><span data-testid="cart-total">{formatPrice(total)}</span></div>
                    </div>
                    <button
                        data-testid="checkout-btn"
                        onClick={() => navigate(`/checkout${applied ? `?coupon=${applied.code}` : ""}`)}
                        className="w-full mt-6 bg-gold text-white py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-gold-hover transition-colors"
                    >
                        Proceed to Checkout
                    </button>
                </div>
            </div>
        </div>
    );
}
